import { parse, format, addDays } from 'date-fns';

export interface IHotelSearchCriteria {
  adults?: number;
  checkin?: string;
  checkout?: string;
  id: string;
  searchId?: string;
  hotelId?: string;
  geo?: string;
  coordinates?: { lat: number; lon: number };
  children?: number[];
}

export class HotelSearchCriteria implements IHotelSearchCriteria {
  adults: number;
  checkin: string;
  checkout: string;
  id: string;
  searchId?: string;
  hotelId?: string;
  geo?: string;
  coordinates?: { lat: number; lon: number };
  children?: number[];

  static defaultAdults: number = 1;
  static datePattern: string = 'YYYY-MM-DD';
  static defaultCheckin: string = format(
    addDays(new Date(), 5),
    HotelSearchCriteria.datePattern,
  );
  static defaultCheckout: string = format(
    addDays(new Date(), 6),
    HotelSearchCriteria.datePattern,
  );

  constructor(payload?: IHotelSearchCriteria) {
    this.adults = payload?.adults ?? HotelSearchCriteria.defaultAdults;
    this.checkin = payload?.checkin ?? HotelSearchCriteria.defaultCheckin;
    this.checkout = payload?.checkout ?? HotelSearchCriteria.defaultCheckout;
    this.id = payload?.id ?? '';
    this.searchId = payload?.searchId ?? '';
    this.geo = payload?.geo ?? '';
    this.hotelId = payload?.hotelId ?? '';
    this.coordinates = payload?.coordinates ?? null;
    this.children = payload?.children ?? [];
  }

  getBookingWindow() {
    return Math.round(
      (parse(this.checkin).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24),
    );
  }

  getNights() {
    return Math.round(
      (parse(this.checkout).getTime() - parse(this.checkin).getTime()) /
        (1000 * 60 * 60 * 24),
    );
  }

  baseRequestBody() {
    return {
      checkin: this.checkin,
      checkout: this.checkout,
      guests: this.adults,
    };
  }

  itsHotelId = (): boolean => {
    return (
      this.id.includes('BE') || this.id.includes('B_') || this.id.includes('E_')
    );
  };

  hotelRequestBody() {
    const base = this.baseRequestBody();
    base['id'] = this.id;
    base['user_country'] = 'GB';
    if (this.children?.length > 0) {
      base['children'] = this.children.join(',');
    }

    if (this.searchId) base['search_id'] = this.searchId;
    return base;
  }

  inited() {
    return this.checkin && this.checkout && this.adults;
  }

  copyWith(payload: Partial<IHotelSearchCriteria>) {
    return new HotelSearchCriteria({ ...this, ...payload });
  }

  isDefaultDates() {
    return (
      this.checkin === HotelSearchCriteria.defaultCheckin &&
      this.checkout === HotelSearchCriteria.defaultCheckout
    );
  }

  getRouteQuery(): QueryParams.Base | QueryParams.Search {
    const query: QueryParams.Search = {
      in: this.checkin ? this.checkin : HotelSearchCriteria.defaultCheckin,
      out: this.checkout ? this.checkout : HotelSearchCriteria.defaultCheckout,
    };

    if (this.adults != HotelSearchCriteria.defaultAdults) {
      query.guests = this.adults;
    }

    const children = this.children.filter(it => typeof it === 'number');

    if (children.length > 0) {
      query.childs = children.join(',');
    }

    if (this.hotelId) {
      query.hotelId = this.hotelId;
    }

    return query;
  }
}

export interface SearchRequestBase {
  min_price?: number;
  max_price?: number;
  // min_stars?: number;
  min_rating?: number;
  is_genius?: boolean;
  free_cancellation?: boolean;
  free_breakfast?: boolean;
  pay_later?: boolean;
  pay_online?: boolean;
  amenities?: string;
  accommodation_types?: string;
  reward_programs?: string;
  sort?: number; // Sorting order: 0 -- best match (default), 1 -- highest rating, 2 -- lowest price, 3 -- highest savings
  stars?: number[];
}

export interface InitRequest extends SearchRequestBase {
  mode: number; // 0 - point, 1 - map's viewport

  lat?: number;
  lon?: number;
  radius?: number;

  lat1?: number;
  lon1?: number;
  lat2?: number;
  lon2?: number;

  id: string; // suggest element id
  checkin: string; // YYYY-MM-DD
  checkout: string;
  guests: number;
  children?: string; // array join with comma

  user_country?: string; // default GB
  currency?: string;

  city_id?: number;
  destination_country?: string;
  how_many?: string;

  pricing_mode: number; // 0 - business (default), 1 - personal
}

export interface FetchRequest extends SearchRequestBase {
  from: number;
  how_many?: number;
  room_id: string;
}

export namespace QueryParams {
  export interface Base extends Record<string, string | number> {
    in: string;
    out: string;
    guests?: number;
    childs?: string;
    repeat?: number;
  }

  export interface Search extends Base {
    destintaion?: string;
    hotelId?: string;
    hotel?: string;
    type?: string;
    city?: string;
    state?: string;
    country?: string;
    // filters?: Filter.Query;
    currency?: string;
    locale?: string;
  }
}
